import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { Component, EventEmitter, Input, Output, input, model } from '@angular/core';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { iif } from 'rxjs';
import { ZerosixError } from '@core/models/zerosix.model';
import { ZEROSIX_ERRORS } from '@config/values/zerosix-errors.values';
import { getAllCountries } from '@shared/components/input-country-phone/config/countries.config';

@Component({
  selector: 'app-zerosix-phone-number-step',
  templateUrl: './zerosix-phone-number-step.component.html',
})
export class ZerosixPhoneNumberStepComponent extends DestroyerBase {
  readonly phone = model<string>('');
  readonly useLocalFormat = input<boolean>(false);
  readonly defaultRegionCode = input<string>('fr');

  @Output() onClose = new EventEmitter<Event>();
  @Output() goToNextStep = new EventEmitter<void>();
  @Output() goToAuthenticationStep = new EventEmitter<void>();
  @Output() goToAccountNotFoundStep = new EventEmitter<void>();
  @Output() goToKioskAccountNotFoundStep = new EventEmitter<void>();

  @Input() accentColor?: ContentBuilderFieldColors;

  isValidating = false;
  errorMessage?: string;

  kioskParameters = this.settingsSelector.kiosk;

  constructor(
    private readonly zerosixService: ZerosixService,
    private readonly settingsSelector: SettingsStoreSelector,
  ) {
    super();
  }

  onRegister(): void {
    if (this.isValidating || !this.phone()) {
      return;
    }
    this.isValidating = true;
    this.errorMessage = undefined;
    this.zerosixService
      .isZerosixOtpIdentificationRequired$()
      .pipe(
        switchMap(isOtpRequired => {
          return iif(
            () => isOtpRequired,
            this.zerosixService.sendOtp$(this.phone()),
            this.zerosixService.attachUserByPhone$({
              phoneNumber: this.phone(),
            }),
          );
        }),
        takeUntil(this._destroyerRef),
        tap(response => {
          if (response) {
            this.goToNextStep.next();
          } else {
            this.goToAuthenticationStep.next();
          }
        }),
      )
      .subscribe({
        error: error => {
          this.handleErrorResponse(error.error, this.phone());
        },
        complete: () => (this.isValidating = false),
      });
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.phone();
    this.phone.set(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.phone();
    const defaultRegionCode = this.defaultRegionCode() || 'fr';

    // Get the default dial code based on the region
    const defaultDialCode = this.getDefaultDialCode(defaultRegionCode);

    // Don't allow deletion if it would remove part of the default dial code
    // or if the current value is already just the dial code
    if (currentValue.length <= defaultDialCode.length || currentValue === defaultDialCode) {
      return;
    }

    this.phone.set(currentValue.slice(0, -1));
  }

  private getDefaultDialCode(regionCode: string): string {
    // Get all countries and create a map from iso2 to dial code
    const countries = getAllCountries();
    const country = countries.find(c => c.iso2.toLowerCase() === regionCode.toLowerCase());

    if (country) {
      return `+${country.dialCode}`;
    }

    // Default to France if region code not found
    return '+33';
  }

  private handleErrorResponse(error: ZerosixError, _phone: string): void {
    if (error.class === 'ZerosixException') {
      if (error.code === 'ZEROSIX_MEMBER_NOT_FOUND') {
        this.kioskParameters.isKioskLayout
          ? this.goToKioskAccountNotFoundStep.next()
          : this.goToAccountNotFoundStep.next();
        return;
      }
      this.errorMessage = ZEROSIX_ERRORS[error.code];
      return;
    }
    this.errorMessage = 'errors.generic-error';
  }

  public handleKeyDown(event: KeyboardEvent): void {
    // Prevent default action for Enter key to avoid triggering the first button
    if (event.key === 'Enter') {
      event.preventDefault();
      // Optionally trigger form submission if the form is valid
      if (this.phone()) {
        this.onRegister();
      }
    }
  }
}
